import * as React from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, ListFilter } from "lucide-react";
import { Button } from "@/components/ui";
import { Popover } from "@/components/ui/popover";
import {
  ReviewSentimentFilterButtons,
  ReviewSourceFilterButtons,
  ReviewTriageFilterButtons,
  ReviewTimeFrameFilter,
  ignorePopoverFocusOutOnChange,
} from "@/components/reviews";
import { cn } from "@/lib/utils";
import type { ReviewFilterOrderOptions, ReviewFilterSortOptions } from "@/services/bedrock";
import { ColumnFilterShell } from "./ColumnFilterShell";
import { CoachRatingFilterFields } from "./CoachRatingFilterFields";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";
import { OverviewScopePickerContent } from "../../Overview/OverviewScopePicker";

type ColumnFilterRenderProps = {
  open: boolean;
  close: () => void;
};

type ReviewsColumnHeaderProps = {
  title: string;
  sortColumn?: ReviewFilterSortOptions;
  currentSort?: ReviewFilterSortOptions;
  currentOrder?: ReviewFilterOrderOptions;
  onSortChange?: (sort: ReviewFilterSortOptions) => void;
  filterActive?: boolean;
  filterContent?: React.ReactNode | ((props: ColumnFilterRenderProps) => React.ReactNode);
  className?: string;
};

export const ReviewsColumnHeader = React.memo(function ReviewsColumnHeader({
  title,
  sortColumn,
  currentSort,
  currentOrder,
  onSortChange,
  filterActive = false,
  filterContent,
  className,
}: ReviewsColumnHeaderProps) {
  const [filterOpen, setFilterOpen] = React.useState(false);
  const isSorted = Boolean(sortColumn && currentSort === sortColumn);
  const SortIcon = !isSorted ? ArrowUpDown : currentOrder === "asc" ? ArrowUp : ArrowDown;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {sortColumn && onSortChange ? (
        <button
          type="button"
          className="group flex cursor-pointer items-center gap-1 rounded-sm font-medium whitespace-nowrap"
          onClick={e => {
            e.stopPropagation();
            onSortChange(sortColumn);
          }}
          aria-label={`Sort by ${title}`}
        >
          <SortIcon
            className={cn("size-3.5 shrink-0", isSorted ? "text-primary" : "text-slate-400 group-hover:text-inherit")}
            strokeWidth={isSorted ? 3 : 2}
          />
          {title}
        </button>
      ) : (
        <span className="font-medium">{title}</span>
      )}
      {filterContent ? (
        <Popover open={filterOpen} onOpenChange={ignorePopoverFocusOutOnChange(setFilterOpen)}>
          <Popover.Trigger
            render={
              <Button
                type="button"
                variant={filterActive ? "default" : "ghost"}
                size="icon-sm"
                className="size-6"
                aria-label={filterActive ? `Filter ${title} (active)` : `Filter ${title}`}
                aria-pressed={filterActive}
                onClick={e => e.stopPropagation()}
              >
                <ListFilter className="size-3.5" />
              </Button>
            }
          />
          {typeof filterContent === "function"
            ? filterContent({ open: filterOpen, close: () => setFilterOpen(false) })
            : filterContent}
        </Popover>
      ) : null}
    </div>
  );
});

function PlayerColumnFilterContent({ open, close }: ColumnFilterRenderProps) {
  const { players, playerId, segmentationIds, setPlayerId, setSegmentationIds } = useCoachReviewsFilters();
  const hasFilters = Boolean(playerId || segmentationIds.length);
  const segmentationDetailId = segmentationIds[0] ?? null;

  return (
    <Popover.Content
      align="start"
      side="bottom"
      sideOffset={6}
      initialFocus={false}
      className="w-[22rem] max-w-[calc(100vw-2rem)] gap-3 p-3 font-sans shadow-xl border border-slate-100 bg-white rounded-xl"
    >
      {hasFilters ? (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 px-2 text-xs"
            onClick={() => {
              players.reset();
              setSegmentationIds([]);
            }}
          >
            Clear
          </Button>
        </div>
      ) : null}
      <OverviewScopePickerContent
        enabled={open}
        playerId={playerId}
        segmentationDetailId={segmentationDetailId}
        onSelectPlayer={id => {
          setSegmentationIds([]);
          setPlayerId(id);
          close();
        }}
        onSelectSegmentation={id => {
          players.reset();
          setSegmentationIds([id]);
          close();
        }}
      />
    </Popover.Content>
  );
}

/** Sorting is only surfaced once the Coach picks one; unmarked means the default API sort. */
function useSortHeaderProps() {
  const { filters, toggleSort } = useCoachReviewsFilters();

  return {
    currentSort: filters.sort ?? undefined,
    currentOrder: filters.order,
    onSortChange: toggleSort,
  };
}

export function PlayerColumnHeader() {
  const { playerId, segmentationIds } = useCoachReviewsFilters();
  const sortProps = useSortHeaderProps();

  return (
    <ReviewsColumnHeader
      title="Player"
      sortColumn="player_name"
      {...sortProps}
      filterActive={Boolean(playerId || segmentationIds.length)}
      filterContent={props => <PlayerColumnFilterContent {...props} />}
    />
  );
}

export function SourceColumnHeader() {
  const { filters, setSourceSystem } = useCoachReviewsFilters();
  const filterActive = Boolean(filters.sourceSystem);

  return (
    <ReviewsColumnHeader
      title="Source"
      className="justify-center w-35"
      filterActive={filterActive}
      filterContent={
        <ColumnFilterShell title="Source Platform" onClear={filterActive ? () => setSourceSystem(null) : undefined}>
          <ReviewSourceFilterButtons variant="expandable" value={filters.sourceSystem} onChange={setSourceSystem} />
        </ColumnFilterShell>
      }
    />
  );
}

export function RatingColumnHeader() {
  const { filters, setRatingType, setRatingValue, setNpsCategory, setRecommendation } = useCoachReviewsFilters();
  const sortProps = useSortHeaderProps();

  const sortColumn =
    filters.ratingType === "star"
      ? "star_rating"
      : filters.ratingType === "nps"
      ? "nps_score"
      : filters.ratingType === "ten_point"
      ? "ten_point_rating"
      : undefined;

  const filterActive =
    Boolean(filters.ratingType) ||
    filters.ratingValue != null ||
    Boolean(filters.npsCategory) ||
    Boolean(filters.recommendation);

  return (
    <ReviewsColumnHeader
      title="Rating"
      className="justify-center w-35"
      sortColumn={sortColumn}
      {...sortProps}
      onSortChange={sortColumn ? sortProps.onSortChange : undefined}
      filterActive={filterActive}
      filterContent={
        <ColumnFilterShell title="Rating Type" onClear={filterActive ? () => setRatingType(null) : undefined}>
          <CoachRatingFilterFields
            ratingType={filters.ratingType}
            onRatingTypeChange={setRatingType}
            ratingValue={filters.ratingValue}
            onRatingValueChange={setRatingValue}
            npsCategory={filters.npsCategory}
            onNpsCategoryChange={setNpsCategory}
            recommendation={filters.recommendation}
            onRecommendationChange={setRecommendation}
            valueFilterClassName="gap-3 border-t border-slate-100 pt-3"
          />
        </ColumnFilterShell>
      }
    />
  );
}

export function SentimentColumnHeader() {
  const { filters, setSentiment } = useCoachReviewsFilters();
  const sortProps = useSortHeaderProps();
  const filterActive = Boolean(filters.sentiment);

  return (
    <ReviewsColumnHeader
      title="Sentiment"
      className="justify-center w-35"
      sortColumn="sentiment"
      {...sortProps}
      filterActive={filterActive}
      filterContent={
        <ColumnFilterShell title="Sentiment" onClear={filterActive ? () => setSentiment(null) : undefined}>
          <ReviewSentimentFilterButtons variant="row" value={filters.sentiment} onChange={setSentiment} />
        </ColumnFilterShell>
      }
    />
  );
}

export function ReviewerColumnHeader() {
  const sortProps = useSortHeaderProps();

  return <ReviewsColumnHeader title="Reviewer" sortColumn="display_name" {...sortProps} />;
}

export function ContentColumnHeader() {
  const sortProps = useSortHeaderProps();

  return <ReviewsColumnHeader title="Content" sortColumn="content" {...sortProps} />;
}

export function ReplyStatusColumnHeader() {
  const { filters, setTriage } = useCoachReviewsFilters();
  const filterActive = Boolean(filters.triage);

  return (
    <ReviewsColumnHeader
      title="Reply Status"
      className="justify-center w-35"
      filterActive={filterActive}
      filterContent={
        <ColumnFilterShell title="Reply Status" onClear={filterActive ? () => setTriage(null) : undefined}>
          <ReviewTriageFilterButtons variant="row" value={filters.triage} onChange={setTriage} />
        </ColumnFilterShell>
      }
    />
  );
}

export function DateColumnHeader() {
  const { filters, setDatePreset } = useCoachReviewsFilters();
  const sortProps = useSortHeaderProps();
  const filterActive = Boolean(filters.datePreset || filters.startDate || filters.endDate);

  return (
    <ReviewsColumnHeader
      title="Date"
      sortColumn="effective_date"
      {...sortProps}
      filterActive={filterActive}
      filterContent={
        <ColumnFilterShell
          title="Date"
          onClear={filterActive ? () => setDatePreset({ preset: null, startDate: null, endDate: null }) : undefined}
        >
          <ReviewTimeFrameFilter
            preset={filters.datePreset}
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={setDatePreset}
            showLabel={false}
          />
        </ColumnFilterShell>
      }
    />
  );
}
